'use client';

import AuthPanel from '@/components/auth/AuthPanel';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useSignOut } from '@/hooks/use-sign-out';
import { isProtectedRoute } from '@/utils/navigation';
import { CheckVerifySessionResponse } from '@/utils/type';
import { Modal } from '@mantine/core';
import { usePathname, useRouter } from 'next/navigation';
import { createContext, Dispatch, ReactNode, useCallback, useContext, useEffect, useReducer } from 'react';

type AuthAction = {
  type: 'OPEN_MODAL' | 'CLOSE_MODAL' | '2FA_ACCEPT' | '2FA_REJECT';
  [prop: string]: any;
};

export type AuthModalTab = 'login' | 'signup' | 'resetpass' | 'verify-otp';

type AuthContextState = {
  modalOpened: boolean;
  defaultTab: AuthModalTab;
  twoFactorAuthenticated: boolean;
  sessionChecked: boolean;
  isExplicitLogin: boolean;
  isFirstLogin: boolean;
  isVerified: boolean;
  isTrustedDevice: boolean;
}

const initialState: AuthContextState = {
  modalOpened: false,
  defaultTab: 'login' as AuthModalTab,
  twoFactorAuthenticated: false,
  sessionChecked: false,
  isExplicitLogin: false,
  isFirstLogin: false,
  isVerified: false,
  isTrustedDevice: false,
};

const reducer = (state: AuthContextState, action: AuthAction) => {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        ...state,
        modalOpened: true,
        defaultTab: action.payload?.defaultTab ?? 'login',
        isExplicitLogin: action.payload?.defaultTab === 'login',
        isFirstLogin: action.payload?.isFirstLogin ?? false,
      };
    case 'CLOSE_MODAL':
      const hasPendingOTP = action.payload?.hasPendingOTP ?? false;
      return { 
        ...state, 
        modalOpened: false,
        sessionChecked: !hasPendingOTP
      };
    case '2FA_ACCEPT':
      return { 
        ...state, 
        twoFactorAuthenticated: true, 
        sessionChecked: true, 
        modalOpened: false,
        isVerified: true,
        isTrustedDevice: action.payload?.isTrustedDevice || false
      };
    case '2FA_REJECT':
      return { 
        ...state, 
        twoFactorAuthenticated: false, 
        sessionChecked: false,
        isVerified: false
      };
    default:
      return state;
  }
};

type AuthContextType = {
  state: AuthContextState;
  dispatch: Dispatch<AuthAction>
}

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [currentUser, authStatus] = useCurrentUser();
  const router = useRouter();
  const pathname = usePathname();
  const signOut = useSignOut();

  const checkSession = useCallback(async () => {
    try {
      if (!currentUser) {
        dispatch({ type: '2FA_REJECT' });
        return;
      }

      // Check if this device is trusted for this user
      const isTrustedDevice = localStorage.getItem(`trusted_device_${currentUser.uid}`) === 'true';
      const isFirstLoginComplete = localStorage.getItem(`first_login_complete_${currentUser.uid}`) === 'true';
      const isVerified = localStorage.getItem(`2fa_verified_${currentUser.uid}`) === 'true';
      const hasPendingOTP = localStorage.getItem(`pending_otp_${currentUser.uid}`) === 'true';

      // If user has a pending OTP, they must complete it
      if (hasPendingOTP && !isVerified && !isTrustedDevice) {
        dispatch({ type: '2FA_REJECT' });
        if (isProtectedRoute(pathname)) {
          dispatch({ 
            type: 'OPEN_MODAL', 
            payload: { 
              defaultTab: 'verify-otp',
              isFirstLogin: !isFirstLoginComplete 
            } 
          });
        }
        return;
      }

      // If device is trusted or verification is complete, accept 2FA
      if (isTrustedDevice || isVerified) {
        localStorage.removeItem(`pending_otp_${currentUser.uid}`);
        dispatch({ 
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice,
            isVerified: true
          }
        });
        return;
      }

      // Check if user is authenticated through Google
      const isGoogleUser = currentUser.providerData[0]?.providerId === 'google.com';

      if (isGoogleUser) {
        localStorage.removeItem(`pending_otp_${currentUser.uid}`);
        dispatch({ 
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice: true,
            isVerified: true
          }
        });
        return;
      }

      const token = await currentUser.getIdToken();
      
      // Verify the session
      const response = await fetch('/api/auth/verify-session', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        dispatch({ type: '2FA_REJECT' });
        if (isProtectedRoute(pathname)) {
          router.push("/");
        }
        return;
      }

      const checkSessionResponse = await response.json() as CheckVerifySessionResponse;

      if (checkSessionResponse.requiresLogin) {
        dispatch({ type: '2FA_REJECT' });
        if (isProtectedRoute(pathname)) {
          router.push("/");
        }
      } else if (checkSessionResponse.requiresTwoFactor) {
        // Mark that user has a pending OTP
        localStorage.setItem(`pending_otp_${currentUser.uid}`, 'true');
        
        dispatch({ type: '2FA_REJECT' });
        if (isProtectedRoute(pathname)) {
          dispatch({ 
            type: 'OPEN_MODAL', 
            payload: { 
              defaultTab: 'verify-otp',
              isFirstLogin: !isFirstLoginComplete 
            } 
          });
        }
      } else {
        // Clear pending OTP status if verification successful
        localStorage.removeItem(`pending_otp_${currentUser.uid}`);
        
        dispatch({ 
          type: '2FA_ACCEPT',
          payload: {
            isTrustedDevice,
            isVerified: true
          }
        });
      }
    } catch (error) {
      console.error('Error checking session:', error);
      dispatch({ type: '2FA_REJECT' });
      if (isProtectedRoute(pathname)) {
        router.push("/");
      }
    }
  }, [currentUser, dispatch, pathname, router]);

  // Check session on mount and when user changes
  useEffect(() => {
    if (currentUser && !state.sessionChecked) {
      checkSession();
    }
  }, [currentUser, state.sessionChecked, checkSession]);

  // Handle modal state changes
  useEffect(() => {
    if (state.modalOpened && currentUser) {
      checkSession();
    }
  }, [state.modalOpened, currentUser, checkSession]);

  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
      <Modal
        centered
        size={450}
        padding={0}
        radius={20}
        withCloseButton={false}
        opened={state.modalOpened}
        onClose={() => {
          const hasPendingOTP = currentUser ? 
            localStorage.getItem(`pending_otp_${currentUser.uid}`) === 'true' : 
            false;
          dispatch({ 
            type: 'CLOSE_MODAL',
            payload: { hasPendingOTP }
          });
        }}
      >
        <AuthPanel
          defaultTab={state.defaultTab}
          onCloseRequest={() => {
            const hasPendingOTP = currentUser ? 
              localStorage.getItem(`pending_otp_${currentUser.uid}`) === 'true' : 
              false;
            dispatch({ 
              type: 'CLOSE_MODAL',
              payload: { hasPendingOTP }
            });
          }}
        />
      </Modal>
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const authContext = useContext(AuthContext);
  if (authContext === null) {
    throw new Error("Must use inside auth context provider.");
  }

  return authContext;
}
